import React from 'react';

/**
 * Kanonischer Status-Chip des Design-Systems (Phase 5).
 *
 * Ersetzt ~93 handgebaute Chips in ~50 verschiedenen Signaturen.
 *
 * Farbregel aus TOKENS.md: Die Statusfarbe traegt Flaeche, Kontur und Icon —
 * der **Text bleibt `text-foreground`**. Grund ist Kontrast: `text-warning`
 * bzw. `text-success` (= *-600) auf `{state}-soft` (= *-100) verfehlen im
 * Light-Theme AA (amber ~2.4:1, green ~3.2:1).
 *
 * - tone: neutral | success | warning | danger | info | primary
 * - size: sm | md | lg (13px, Session-Karte)
 * - icon: optionale lucide-Komponente (erbt die Statusfarbe)
 */

const TONES = {
  neutral: 'bg-surface-raised border-border',
  success: 'bg-success-soft border-success/40',
  warning: 'bg-warning-soft border-warning/40',
  danger: 'bg-danger-soft border-danger/40',
  info: 'bg-info-soft border-info/40',
  primary: 'bg-primary-soft border-primary/40',
};

const ICON_TONES = {
  neutral: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  primary: 'text-primary',
};

const SIZES = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-xs sm:text-sm gap-1.5',
  // Wie die Badges der Session-Karte im Spot (Motion-Kit `.eo-badge`, 13px).
  lg: 'px-2.5 py-[3px] text-[13px] gap-[5px]',
};

const ICON_SIZES = {
  sm: 'w-3 h-3',
  md: 'w-3 h-3',
  lg: 'w-[13px] h-[13px]',
};

export const badgeClasses = (tone = 'neutral', size = 'sm', className = '') =>
  [
    'inline-flex items-center rounded-full border font-medium text-foreground whitespace-nowrap',
    TONES[tone] || TONES.neutral,
    SIZES[size] || SIZES.sm,
    className,
  ]
    .filter(Boolean)
    .join(' ');

const Badge = ({ tone = 'neutral', size = 'sm', icon: Icon, className = '', children, ...rest }) => (
  <span className={badgeClasses(tone, size, className)} {...rest}>
    {Icon && (
      <Icon
        className={`${ICON_SIZES[size] || ICON_SIZES.sm} shrink-0 ${ICON_TONES[tone] || ICON_TONES.neutral}`}
        aria-hidden="true"
      />
    )}
    {children}
  </span>
);

export default Badge;
